import { useCallback, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  Button,
  Cell,
  Input,
  List,
  Section,
  Spinner,
  Switch,
  Text,
} from "@telegram-apps/telegram-ui";

import { getDefaultPath } from "../access";
import { api } from "../api";
import { useAppData } from "../AppData";
import { useBackButton } from "../hooks/useBackButton";
import { useMainButton } from "../hooks/useMainButton";
import { DEFAULT_JOB_OPTIONS, type JobOptions, type OperationCategory } from "../types";

const URL_RE = /^https?:\/\/docs\.google\.com\/spreadsheets\//;

const CATEGORY_PATH: Record<OperationCategory, string> = {
  calc: "/calc",
  views: "/views",
  report: "/report",
};

type CountKey =
  | "count_views"
  | "count_interactions"
  | "count_subscribers"
  | "count_text_length";

const CALCALL_FIELDS: { key: CountKey; label: string; subtitle: string }[] = [
  { key: "count_views", label: "Просмотры", subtitle: "Колонка «Просмотры»." },
  { key: "count_interactions", label: "Взаимодействия", subtitle: "Колонка «Взаимодействия»." },
  { key: "count_subscribers", label: "Подписчики", subtitle: "Колонка «Количество подписчиков»." },
  { key: "count_text_length", label: "Длина текста", subtitle: "Колонка «Количество знаков»." },
];

type Props = {
  // Явный id операции + режим «корень таба»: форма встроена в CategoryTab как
  // единственная операция раздела (нижнее меню есть, кнопки «Назад» нет).
  // Без props форма работает как отдельная страница /op/:opId (drill-down).
  opId?: string;
  asTabRoot?: boolean;
};

export function OperationForm({ opId: opIdProp, asTabRoot = false }: Props = {}) {
  const params = useParams<{ opId: string }>();
  const opId = opIdProp ?? params.opId;
  const navigate = useNavigate();
  const { me, operations, loading } = useAppData();

  const op = operations.find((o) => o.id === opId) ?? null;
  const backPath = op ? CATEGORY_PATH[op.category] : getDefaultPath(me, operations);

  const [tableUrl, setTableUrl] = useState("");
  const [options, setOptions] = useState<JobOptions>(DEFAULT_JOB_OPTIONS);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Корень таба переключается нижним меню, поэтому «Назад» там не нужна.
  useBackButton(!asTabRoot, () => navigate(backPath));

  const valid = URL_RE.test(tableUrl.trim());

  const submit = useCallback(async () => {
    if (!op || !valid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { job_id } = await api.createJob(op.id, tableUrl.trim(), options);
      navigate(`/job/${job_id}`);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }, [op, tableUrl, valid, submitting, navigate, options]);

  // В корне таба нижнее меню Telegram/веба конфликтует с плавающим меню, поэтому
  // там кнопку запуска рисуем прямо в форме (как на вкладке «Заявка»).
  useMainButton({
    text: submitting ? "Запускаю..." : "Запустить",
    visible: !!op && !asTabRoot,
    enabled: valid && !submitting,
    loading: submitting,
    onClick: submit,
  });

  if (!loading && !op) {
    return <Navigate to={getDefaultPath(me, operations)} replace />;
  }
  if (!op) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="m" />
      </div>
    );
  }

  return (
    <div className={asTabRoot ? "page page--with-nav" : "page"}>
      <List>
        <Section header={op.title}>
          <div style={{ padding: "8px 16px" }}>
            <Text>{op.description}</Text>
          </div>
        </Section>

        <Section header="Ссылка на таблицу">
          <Input
            value={tableUrl}
            onChange={(e) => setTableUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
            status={tableUrl && !valid ? "error" : "default"}
          />
          <div style={{ padding: "8px 16px" }}>
            <span className="muted">
              Поделитесь таблицей с сервисным аккаунтом бота с правами редактора.
            </span>
          </div>
        </Section>

        {op.id === "calcall" && (
          <Section header="Что подсчитывать">
            {CALCALL_FIELDS.map((f) => (
              <Cell
                key={f.key}
                Component="label"
                subtitle={f.subtitle}
                after={
                  <Switch
                    checked={options[f.key]}
                    onChange={(e) =>
                      setOptions((prev) => ({
                        ...prev,
                        [f.key]: e.target.checked,
                      }))
                    }
                  />
                }
              >
                {f.label}
              </Cell>
            ))}
          </Section>
        )}

        {op.has_view_counting && (op.id !== "calcall" || options.count_views) && (
          <Section header="Опции">
            <Cell
              Component="label"
              multiline
              subtitle="Значения просмотров больше 1000 округляются вниз до сотен (например, 1234 → 1200)."
              after={
                <Switch
                  checked={options.round_views}
                  onChange={(e) =>
                    setOptions((prev) => ({
                      ...prev,
                      round_views: e.target.checked,
                    }))
                  }
                />
              }
            >
              Округлять просмотры
            </Cell>
          </Section>
        )}

        <Section header="Нужные колонки в таблице">
          <div style={{ padding: "8px 16px" }}>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {op.required_columns.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {submitError && (
          <div style={{ padding: "8px 16px" }}>
            <Banner type="section" header="Не удалось запустить" description={submitError} />
          </div>
        )}

        {asTabRoot && (
          <div style={{ padding: "8px 16px" }}>
            <Button
              size="l"
              stretched
              disabled={!valid || submitting}
              loading={submitting}
              onClick={submit}
            >
              {submitting ? "Запускаю..." : "Запустить"}
            </Button>
          </div>
        )}
      </List>
    </div>
  );
}
